/**
 * Sentry Error Boundary
 *
 * Mirrors the old project's `src/components/common/ErrorBoundary.tsx`:
 * - Class component (componentDidCatch)
 * - Sentry.withScope → setContext('errorBoundary', { componentStack }) → captureException
 * - Shows a friendly fallback UI with Try Again and Go to Dashboard buttons
 */

import { Component, ErrorInfo, ReactNode } from 'react'
import { Sentry, isSentryEnabled } from '@/config/sentry'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Optional custom fallback UI */
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Caught error:', error)
      console.error('[ErrorBoundary] Component stack:', info.componentStack)
    }

    // Report to Sentry with component stack context
    if (isSentryEnabled) {
      Sentry.withScope((scope) => {
        scope.setContext('errorBoundary', {
          componentStack: info.componentStack,
        })
        Sentry.captureException(error)
      })
    }
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center p-8 text-center">
          <div className="mx-auto max-w-md space-y-4">
            <div className="text-4xl">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900">
              Something went wrong
            </h2>
            <p className="text-sm text-gray-500">
              An unexpected error occurred. Our team has been notified and is
              looking into it.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-4 rounded-lg bg-red-50 p-4 text-left">
                <summary className="cursor-pointer text-sm font-medium text-red-800">
                  Error Details (dev only)
                </summary>
                <pre className="mt-2 overflow-auto text-xs text-red-700">
                  {this.state.error.message}
                  {'\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
            <div className="flex justify-center gap-3 pt-2">
              <button
                onClick={this.handleReset}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => (window.location.href = '/')}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
